import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, Upload, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * PhotoUploader — dual-mode photo input.
 * • Choose file / take photo (camera on mobile via `capture="environment"`)
 * • Paste URL fallback (backwards-compatible with existing URL-based photos)
 *
 * Calls back with the final URL string via `onAdd(url)`. The `kind` prop
 * tags the file in GridFS so an org can later query all photos of a
 * given type. Backend endpoint: POST /api/photos/upload?kind=<kind>.
 */
export const PhotoUploader = ({
  onAdd,
  kind = 'generic',
  showUrlInput = true,
  captureCamera = true,
  disabled = false,
  testIdPrefix = 'photo-uploader',
}) => {
  const fileInputRef = useRef(null);
  const [urlValue, setUrlValue] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFilePick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-selecting the same file
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8 MB)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post(`/photos/upload?kind=${encodeURIComponent(kind)}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onAdd(data.url);
      toast.success('Photo uploaded');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlAdd = () => {
    const url = urlValue.trim();
    if (!url) return;
    onAdd(url);
    setUrlValue('');
  };

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          {...(captureCamera ? { capture: 'environment' } : {})}
          onChange={handleFileChange}
          className="hidden"
          data-testid={`${testIdPrefix}-file-input`}
        />
        <Button
          type="button"
          onClick={handleFilePick}
          disabled={disabled || uploading}
          className="flex-1 bg-slate-900 hover:bg-slate-800"
          data-testid={`${testIdPrefix}-pick-btn`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Camera className="h-4 w-4 mr-2 sm:hidden" />
              <Upload className="h-4 w-4 mr-2 hidden sm:inline-block" />
              <span className="sm:hidden">Take / Choose Photo</span>
              <span className="hidden sm:inline">Upload Photo</span>
            </>
          )}
        </Button>
      </div>

      {showUrlInput && (
        <div className="flex gap-2">
          <Input
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="…or paste image URL"
            className="flex-1"
            data-testid={`${testIdPrefix}-url-input`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleUrlAdd}
            disabled={disabled || !urlValue.trim()}
            data-testid={`${testIdPrefix}-url-add`}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;
